import { ProductSelectEvent, StandardEvents } from '@shopify/events';

const AUTOMATIC_COLORS = {
  red: '#c85f59',
  'army green': '#7e8765',
  'baby pink': '#ead5d9',
  blue: '#7aa0c7',
  'bright green': '#83bc86',
  brown: '#8a6957',
  'christmas green': '#326b4e',
  coffee: '#896957',
  cream: '#eee9df',
  'dark gray': '#777a78',
  'grayish blue': '#9bb4c2',
  khaki: '#bba885',
  'light green': '#c9d9bd',
  'light gray': '#d4d6d4',
  'light pink': '#edd3da',
  lilac: '#cbbada',
  mint: '#b9d9cd',
  'navy blue': '#2d4164',
  'peacock blue': '#3e8191',
  pink: '#eee9e0',
  pumpkin: '#d3803e',
  'rose pink': '#d68ba0',
  'sky blue': '#b4d4e4',
  yellow: '#ead37d',
  apricot: '#e9bd99',
};

class KmVariantOptionConfig extends HTMLElement {
  connectedCallback() {
    this.scheduleApply();
  }

  scheduleApply(attempt = 0) {
    queueMicrotask(() => {
      const applied = this.applyConfiguration();
      if (!applied && attempt < 20 && this.isConnected) {
        window.setTimeout(() => this.scheduleApply(attempt + 1), 100);
      }
    });
  }

  findPicker() {
    const configs = this.closest('[data-km-variant-configs-for]');
    const blockId = configs?.dataset.kmVariantConfigsFor || '';
    if (!configs || !blockId) return null;

    const section = configs.closest('.shopify-section');
    const scopedPickers = section?.querySelectorAll('variant-picker[data-block-id]') || [];
    const scopedMatch = Array.from(scopedPickers).find((picker) => picker.dataset.blockId === blockId);
    if (scopedMatch) return scopedMatch;

    return (
      Array.from(document.querySelectorAll('variant-picker[data-block-id]')).find(
        (picker) => picker.dataset.blockId === blockId
      ) || null
    );
  }

  applyConfiguration() {
    const picker = this.findPicker();
    if (!picker) return false;

    const optionName = (this.dataset.optionName || '').trim().toLowerCase();
    if (!optionName) return false;

    const radios = Array.from(picker.querySelectorAll('input[data-option-name]')).filter(
      (input) => (input.dataset.optionName || '').trim().toLowerCase() === optionName
    );
    const fieldset = radios[0]?.closest('fieldset');
    if (!fieldset) return false;

    this.setStyleVariables(fieldset);

    if (this.dataset.displayStyle === 'visual') {
      this.applyVisualCards(fieldset, radios);
    } else if (this.dataset.displayStyle === 'dropdowns') {
      this.applyDropdown(fieldset, radios);
    } else {
      this.applyTextButtons(fieldset);
    }

    return true;
  }

  setStyleVariables(fieldset) {
    fieldset.style.setProperty('--km-visual-card-columns', this.dataset.columns || '5');
    fieldset.style.setProperty('--km-visual-card-columns-mobile', this.dataset.mobileColumns || '3');
    fieldset.style.setProperty('--km-visual-swatch-size', `${this.dataset.previewSize || 56}px`);
    fieldset.style.setProperty('--km-visual-card-gap', `${this.dataset.cardGap || 8}px`);
    fieldset.style.setProperty('--km-visual-label-size', `${this.dataset.labelSize || 11}px`);
    fieldset.style.setProperty('--km-option-card-radius', `${this.dataset.cardRadius || 8}px`);
  }

  resetFieldset(fieldset) {
    fieldset.classList.remove(
      'variant-option--visual-cards',
      'variant-option--controlled-buttons',
      'km-variant-option--dropdown'
    );
    fieldset.querySelector('.km-variant-dropdown')?.remove();
    for (const label of fieldset.querySelectorAll(':scope > .variant-option__button-label')) {
      label.hidden = false;
      label.classList.remove('variant-option__button-label--visual');
      label.querySelector('.variant-option__visual-preview')?.remove();
      label.querySelector('.variant-option__button-label__pill')?.removeAttribute('hidden');
      const text = label.querySelector('.variant-option__button-label__text');
      text?.classList.remove('variant-option__visual-label');
      text?.removeAttribute('hidden');
    }
  }

  applyVisualCards(fieldset, radios) {
    this.resetFieldset(fieldset);
    fieldset.classList.add('variant-option--visual-cards');

    const valueConfigs = new Map(
      Array.from(this.querySelectorAll('[data-km-option-value]')).map((item) => [
        (item.dataset.kmOptionValue || '').trim().toLowerCase(),
        item,
      ])
    );
    const showLabels = this.dataset.showValueLabels !== 'false';

    for (const input of radios) {
      const label = input.closest('label');
      if (!label) continue;

      label.classList.add('variant-option__button-label--visual');
      label.querySelector('.variant-option__button-label__pill')?.setAttribute('hidden', '');

      const valueName = input.value || '';
      const valueConfig = valueConfigs.get(valueName.trim().toLowerCase());
      const customImageUrl = valueConfig?.dataset.kmImageUrl || '';
      const variantImageUrl = input.dataset.kmVariantImageUrl || '';
      const imageUrl =
        customImageUrl || (this.dataset.fallbackSource === 'variant_image' ? variantImageUrl : '');
      const customColor = valueConfig?.dataset.kmFallbackColor || '';
      const automaticColor = AUTOMATIC_COLORS[valueName.trim().toLowerCase()] || '#d8d8d8';

      const preview = document.createElement('span');
      preview.className = 'variant-option__visual-preview';
      preview.style.setProperty('--km-visual-color', customColor || automaticColor);

      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        preview.append(image);
      }

      input.insertAdjacentElement('afterend', preview);

      const text = label.querySelector('.variant-option__button-label__text');
      if (text) {
        text.classList.add('variant-option__visual-label');
        text.hidden = !showLabels;
      }
    }
  }

  applyDropdown(fieldset, radios) {
    this.resetFieldset(fieldset);
    fieldset.classList.add('km-variant-option--dropdown');

    for (const label of fieldset.querySelectorAll(':scope > .variant-option__button-label')) {
      label.hidden = true;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'variant-option__select-wrapper km-variant-dropdown';

    const select = document.createElement('select');
    select.className = 'variant-option__select';
    select.name = `options[${this.dataset.optionName || ''}]`;
    select.setAttribute('aria-label', this.dataset.optionName || '');

    for (const input of radios) {
      const option = document.createElement('option');
      option.value = input.value;
      option.textContent = input.getAttribute('aria-disabled') === 'true' ? `${input.value} — Unavailable` : input.value;
      option.selected = input.checked;
      option.disabled = input.getAttribute('aria-disabled') === 'true';

      for (const key of ['inputId', 'optionName', 'optionValueId', 'variantId', 'connectedProductUrl']) {
        if (input.dataset[key]) option.dataset[key] = input.dataset[key];
      }
      select.append(option);
    }

    wrapper.append(select);
    fieldset.append(wrapper);
  }

  applyTextButtons(fieldset) {
    this.resetFieldset(fieldset);
    fieldset.classList.add('variant-option--controlled-buttons');
  }
}

if (!customElements.get('km-variant-option-config')) {
  customElements.define('km-variant-option-config', KmVariantOptionConfig);
}

const applyAllKmVariantOptionConfigs = () => {
  for (const config of document.querySelectorAll('km-variant-option-config')) {
    config.scheduleApply();
  }
};

const scheduleAllKmVariantOptionConfigs = () => {
  window.requestAnimationFrame(() => applyAllKmVariantOptionConfigs());
};

if (!window.__kmVariantOptionRuntimeEventsBound) {
  window.__kmVariantOptionRuntimeEventsBound = true;

  document.addEventListener(StandardEvents.productSelect, (event) => {
    const eventPromise = /** @type {ProductSelectEvent} */ (event).promise;
    if (!eventPromise || typeof eventPromise.then !== 'function') return;

    eventPromise.then(scheduleAllKmVariantOptionConfigs, scheduleAllKmVariantOptionConfigs);
  });
}

if (window.Shopify?.designMode && !window.__kmVariantOptionEditorEventsBound) {
  window.__kmVariantOptionEditorEventsBound = true;

  for (const eventName of ['shopify:section:load', 'shopify:section:select', 'shopify:block:select']) {
    document.addEventListener(eventName, scheduleAllKmVariantOptionConfigs);
  }
}
